/**
 * The document more-menu (card 3.1) as data: three groups, in this order —
 *
 *   1. Duplicate (Ctrl D) · Move to… · Pin to sidebar / Unpin
 *   2. Print (Ctrl P) · Export ▸ · Copy link
 *   3. Move to trash (danger)
 *
 * Pure (no React, no icons) so the order, the shortcuts and the keyboard
 * walk are testable on their own; DocumentMoreMenu.js renders it.
 */

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

/** "Ctrl D" / "⌘ D" — the kbd chip text for a Ctrl/Cmd shortcut. */
export const shortcutLabel = (key) => `${isMac() ? '⌘' : 'Ctrl'} ${key}`;

/**
 * @param {{ pinned: boolean, canPin?: boolean, canMove?: boolean }} state
 * @returns {Array<Array<{ id, labelKey, kbd?, danger?, submenu?, disabled? }>>}
 */
export function moreMenuGroups({ pinned = false, canPin = true, canMove = true } = {}) {
  return [
    [
      { id: 'duplicate', labelKey: 'docEditor.menu.duplicate', kbd: shortcutLabel('D') },
      { id: 'move', labelKey: 'docEditor.menu.moveTo', disabled: !canMove },
      { id: 'pin', labelKey: pinned ? 'docEditor.menu.unpin' : 'docEditor.menu.pin', disabled: !canPin },
    ],
    [
      { id: 'print', labelKey: 'docEditor.menu.print', kbd: shortcutLabel('P') },
      { id: 'export', labelKey: 'docEditor.menu.export', submenu: true },
      { id: 'copyLink', labelKey: 'docEditor.menu.copyLink' },
    ],
    [{ id: 'trash', labelKey: 'docEditor.menu.trash', danger: true }],
  ];
}

/** The Export ▸ formats with their one-line descriptions (card 3.2). */
export const EXPORT_FORMATS = Object.freeze([
  { id: 'pdf', labelKey: 'docEditor.export.pdf', descKey: 'docEditor.export.pdfDesc' },
  { id: 'markdown', labelKey: 'docEditor.export.markdown', descKey: 'docEditor.export.markdownDesc' },
  { id: 'html', labelKey: 'docEditor.export.html', descKey: 'docEditor.export.htmlDesc' },
]);

/**
 * The next focus index for a roving-focus menu: arrows wrap, Home/End jump,
 * disabled entries are skipped. `enabled` is a boolean per entry. Returns
 * `current` when there is nothing to move to.
 */
export function nextMenuIndex(enabled, current, key) {
  const n = enabled.length;
  if (!n || !enabled.some(Boolean)) return current;
  const step = (from, dir) => {
    let i = from;
    for (let k = 0; k < n; k += 1) {
      i = (i + dir + n) % n;
      if (enabled[i]) return i;
    }
    return current;
  };
  if (key === 'ArrowDown') return step(current < 0 ? -1 : current, 1);
  if (key === 'ArrowUp') return step(current < 0 ? 0 : current, -1);
  if (key === 'Home') return step(-1, 1);
  if (key === 'End') return step(0, -1);
  return current;
}
